import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Stethoscope,
  ClipboardList,
  Activity,
  AlertTriangle,
  Shield,
  Users,
  BookOpen,
  GraduationCap,
  FileCheck,
  TestTube,
  Syringe,
  UserPlus,
  Calculator,
  ChevronDown,
  X
} from "lucide-react";
import "./suggestion.css";

const groups = [
  { label: "Diagnosis & Assessment", indices: [0, 2, 3, 9] },
  { label: "Treatment & Management", indices: [1, 8, 10, 11] },
  { label: "Knowledge & Education",  indices: [4, 5, 6, 7] },
];

const suggestions = [
  // --- Diagnosis & Assessment ---
  {
    icon: Stethoscope,
    label: "Differential Diagnosis",
    intent: "differential_diagnosis",
    queries: [
      "What are the differential diagnoses for a patient presenting with chest pain and shortness of breath?",
      "What are the differential diagnoses for a patient presenting with progressive solid food dysphagia, esophageal rings on endoscopy, and a history of atopy?",
      "What are the differential diagnoses for recurrent food bolus impaction in the esophagus?",
      "What are the differential diagnoses for post-liver transplant biliary strictures identified 4 months after surgery?",
      "55 year old man with mildly elevated alkaline phosphatase. CT scan shows a mass 7cm x 5cm. Radiology says malignancy vs abscess. How to differentiate between them?",
      "How do you differentiate an abscess vs malignancy on a triple phase CT scan of the liver?",
    ],
  },
  {
    icon: ClipboardList,
    label: "Treatment Planning",
    intent: "treatment_planning",
    queries: [
      "What is the recommended treatment plan for newly diagnosed Type 2 diabetes?",
      "A 28-year-old man with a history of sleeve gastrectomy seven weeks ago is diagnosed with proximal sleeve gastrectomy leak. Imaging confirms this diagnosis on both a computed tomography of the abdomen and an upper gastrointestinal series. He is currently stable with his major complaints being abdominal pain and nausea. A representative upper endoscopy reveals what is seen in Video 1. After a multi-disciplinary discussion with the bariatric surgery team, the decision is made to proceed with endoscopic internal drainage with double pigtails. At this time, which of the following is the best therapy in conjunction with endoscopic internal drainage? A. Conversion to Roux-en-Y gastric bypass B. Endoscopic retrograde cholangiopancreatography C. Endoscopic pneumatic balloon dilation D. Non-steroidal anti-inflammatory agents",
      "A 36-year-old man with history of asthma and eczema presents to the emergency department with a food bolus obstruction. After eating at a barbeque restaurant, he feels a piece of pulled pork sandwich stuck in his esophagus and points to a location in the substernal region. He has been unable to swallow saliva. He has had several similar incidents in the past, but they always resolved quickly. He reports a normal endoscopy six months ago. Endoscopy reveals a meat bolus impaction in the esophagus. After you dislodge the bolus, you notice esophageal rings. Which of the following is the most appropriate recommendation? A. Obtain esophageal biopsy during same endoscopy after food impaction removal. B. Recommend avoidance of meat. C. Repeat upper endoscopy in four to six weeks. D. Obtain serum tissue transglutaminase (tTg)-IgA and total IgA.",
      "You are seeing a 50-year-old woman for intermittent solid food and pill dysphagia. She endorses symptoms of dysphagia as well as glossitis and cheilitis for several months. She denies choking and weight loss. On laboratory studies, you note a hemoglobin of 11.5 g/dL, iron saturation of 7%, and a ferritin of 20 µg/L. She denies overt gastrointestinal bleeding. Colonoscopy is unrevealing. On upper endoscopy, you notice this finding in the cervical esophagus which is consistent with esophageal web. After obtaining the biopsy specimen of the cervical esophageal mucosa, the lesion is disrupted. Gastric and duodenal biopsies are normal. Which of the following interventions should be offered next to the patient for her symptoms? A. Iron supplementation and endoscopic surveillance B. Endoscopic dilatation and proton pump inhibitor therapy C. Endoscopic injection of steroids in the cervical esophagus D. Proton pump inhibitor therapy alone",
      "Should I put an ICD in this patient? 43 yr old with hypertrophic cardiomyopathy with recurrent NSVT and symptoms of fluttering in his heart. He has a history of non small cell lung carcinoma and is undergoing immunotherapy. No history of syncope and no family history of sudden cardiac death. His MRI shows fibrosis less than 15%.",
      "A patient is found to have multiple long-segment strictures in the donor intrahepatic ducts 4 months after transplant. How does the expected success rate and management approach for these findings differ from a single focal stricture at the anastomosis?",
      "What should be the recommendation if a surveillance endoscopy for Barrett's esophagus shows high-grade dysplasia on esophageal biopsies?",
      "What should be the recommendation if a surveillance endoscopy for Barrett's esophagus shows Barrett's mucosa with nodularity?",
      "What should be the recommendation if a surveillance endoscopy for Barrett's esophagus shows Barrett's mucosa with a mass?",
      "How can you treat Barrett's esophagus refractory to radiofrequency ablation?",
      "What dose and frequency of proton-pump inhibitor should a patient with Barrett's esophagus take?",
      "Should patients with Barrett's esophagus be prescribed low-dose (81 mg) aspirin?",
      "What is the treatment for high grade dysplasia?",
      "A 65-year-old Italian woman had an endoscopic submucosal dissection (ESD) performed two weeks ago, with positive horizontal margins. The histology showed a low-risk T1 cancer. Should a repeat endoscopy be done, or should surgery be considered?",
      "55 yr old with asymptomatic paroxysmal atrial fibrillation with LVEF of 40%. Is atrial fibrillation ablation indicated in this patient?",
      "I have a 70 year-old patient with normal LV function with symptomatic paroxysmal atrial fibrillation. Comorbidities include hypertension and diabetes. Should I consider an atrial fibrillation ablation? Should I also consider a watchman device implantation?",
      "65-year-old patient with ischemic cardiomyopathy with LV ejection fraction of 30%. Is primary prevention ICD indicated in this patient? What other factors should I consider in deciding the device type?",
      "65-year-old patient with ischemic cardiomyopathy with LV ejection fraction of 30%. Is primary prevention ICD indicated in this patient? Is a CRTD indicated?",
      "35 yr old with one time episode of atrial fibrillation. No other major comorbidities. How do we manage this patient? Is atrial fibrillation ablation indicated?",
      "Compare metal versus plastic biliary stents for pancreatic cancer.",
      "What are the contraindications and drug interactions for metformin?",
    ],
  },
  {
    icon: Activity,
    label: "Prognosis & Risk",
    intent: "prognosis_risk",
    queries: [
      "What is the prognosis for a patient with stage III colon cancer after surgery?",
      "What is the risk of progression from Barrett's esophagus with low-grade dysplasia to esophageal adenocarcinoma?",
      "What is the estimated risk of sudden cardiac death in a patient with hypertrophic cardiomyopathy and recurrent non-sustained ventricular tachycardia?",
      "What is the long-term prognosis for a patient with atrial fibrillation and a left ventricular ejection fraction of 40%?",
    ],
  },
  {
    icon: AlertTriangle,
    label: "Etiology & Harm",
    intent: "etiology_harm",
    queries: [
      "What are the potential causes of acute kidney injury in hospitalized patients?",
      "A patient who underwent a screening colonoscopy six hours ago, during which a 25 mm polyp was removed, now presents with sudden, severe abdominal pain, fever, nausea, vomiting, and abdominal distension. What could have happened, and what actions would you recommend?",
    ],
  },
  {
    icon: Shield,
    label: "Screening & Prevention",
    intent: "screening_prevention",
    queries: [
      "What are the current screening recommendations for colorectal cancer?",
      "What is the surveillance interval after I remove a 5mm tubular adenoma in a 45 year old?",
      "For an average-risk patient with 1–2 nonadvanced tubular adenomas <10 mm completely removed at a high-quality colonoscopy, what surveillance interval do current USMSTF/ACG/AGA guidelines recommend, and what factors would shorten that interval (e.g., poor prep, incomplete resection, family history)?",
      "Should this patient undergo screening for Barrett's esophagus? 52 year-old Caucasian female with 10 year history of gastroesophageal reflux symptoms, current tobacco smoker and family history of esophageal cancer in her father.",
      "Should this patient undergo screening for Barrett's esophagus? 52 year-old Caucasian male with 10 year history of gastroesophageal reflux symptoms, current tobacco smoker and family history of esophageal cancer in his father.",
      "Should this patient undergo screening for Barrett's esophagus? 45 year-old Caucasian male with 15 year history of gastroesophageal reflux, class II obesity, daily alcohol use, former tobacco smoker.",
      "Should this patient undergo screening for Barrett's esophagus? 65 year-old Caucasian male with no gastroesophageal reflux symptoms, class II obesity, weekly alcohol use, former tobacco smoker and family history of Barrett's esophagus in his father.",
      "Should this patient undergo screening for Barrett's esophagus? 82 year-old Caucasian male with severe coronary artery disease, history of myocardial infarction, weekly symptoms of gastroesophageal reflux, weekly alcohol use, current tobacco smoker.",
      "Should this patient undergo screening for Barrett's esophagus? 55 year-old African American male with daily symptoms of gastroesophageal reflux, class III obesity, former tobacco smoker.",
      "What is the best modality to screen for Barrett's esophagus?",
      "What should be the recommendation if a screening endoscopy for Barrett's esophagus shows severe erosive esophagitis (Los Angeles grade C)?",
      "What should be the recommendation if a screening endoscopy for Barrett's esophagus shows no evidence of Barrett's esophagus?",
      "In what time interval should a surveillance endoscopy be repeated for: Patient with a Barrett's esophagus of 7 cm maximal extent and 3 cm circumferential extent with biopsies showing no evidence of dysplasia.",
      "In what time interval should a surveillance endoscopy be repeated for: Patient with Barrett's esophagus of 1 cm maximal extent and 0 cm circumferential extent with biopsies showing no evidence of dysplasia.",
      "In what time interval should a surveillance endoscopy be repeated for: Patient with Barrett's esophagus of 3 cm maximal extent and 1 cm circumferential extent with biopsies showing low-grade dysplasia.",
      "In what time interval should a surveillance endoscopy be repeated for: Patient with Barrett's esophagus of 2 cm maximal extent and 0 cm circumferential extent with biopsies showing indefinite for dysplasia.",
      "What is the time interval for surveillance of a patient with Barrett's esophagus who completes endoscopic eradication therapy?",
      "A 58-year-old African-Italian woman with a history of smoking had four polyps removed (6 mm, 4 mm, 9 mm, and 8 mm, all tubulo-villous adenomas with low-grade dysplasia) five years ago. Should she undergo surveillance colonoscopy? If so, when?",
      "A 60-year-old Caucasian man, living in an area without an organized screening program, underwent a screening colonoscopy one year ago where no polyps were found. Should he undergo surveillance colonoscopy? If so, when?",
      "A 70-year-old Chinese woman had a colonoscopy six months ago following a positive FIT test, during which a 4 mm tubular adenoma with high-grade dysplasia was removed. Should she undergo surveillance colonoscopy? If so, when?",
      "A 75-year-old Italian woman had a serrated polyp, 9 mm in size and without dysplasia, removed during an endoscopy one month ago. Should she undergo surveillance colonoscopy? If so, when?",
      "A 55-year-old man had a screening colonoscopy three weeks ago following a positive FIT test, during which a 25 mm tubule-villous adenoma with low-grade dysplasia was removed via piecemeal resection. Should he undergo surveillance colonoscopy? If so, when?",
      "A 63-year-old Latin American man had a screening colonoscopy three months ago, during which piecemeal EMR was performed on a 19 mm laterally spreading lesion near the ileocecal valve. The histology showed a tubular adenoma with high-grade dysplasia. Should he undergo surveillance colonoscopy? If so, when?",
      "A 55-year-old Asian man underwent a right hemicolectomy four months ago. What follow-up is appropriate if no pre-operative colonoscopy was done versus if a pre-operative colonoscopy was performed?",
      "I run an internal medicine residency clinic and want to improve colorectal cancer screening rates. How can we implement best practices, and can you give me practical tips?",
    ],
  },
  {
    icon: Users,
    label: "Patient Education",
    intent: "patient_education",
    queries: [
      "How do I explain the importance of medication adherence to a patient with hypertension?",
      "How do I explain Barrett's esophagus and its cancer risk to a newly diagnosed patient?",
      "How do I counsel a patient on proper bowel preparation before their colonoscopy?",
      "How do I explain atrial fibrillation ablation, including its risks and expected benefits, to a patient considering the procedure?",
    ],
  },
  {
    icon: BookOpen,
    label: "General Info",
    intent: "general_info",
    queries: [
      "What is the pathophysiology of heart failure?",
      "What advanced imaging modalities can be used to improve detection of dysplasia in Barrett's esophagus surveillance?",
      "What are the latest technologies to improve detection of dysplasia in Barrett's esophagus surveillance?",
      "What is a triple phase CT scan of the liver and what does each phase show?",
      "What is the pathophysiology behind the differences in abscess and malignancy findings on MRI of the liver? How can I remember them easily?",
    ],
  },
  {
    icon: GraduationCap,
    label: "Continued Medical Education",
    intent: "cme",
    queries: [
      "What are the latest updates in managing atrial fibrillation for continuing education?",
      "What is the treatment for high grade dysplasia according to the guidelines from America and how different is it from Europe guidelines?",
      "55 yr old with asymptomatic paroxysmal atrial fibrillation with LVEF of 40%. Is atrial fibrillation ablation indicated in this patient? What does the latest guidelines indicate?",
      "What is the diagnostic accuracy of FNB versus FNA needles for EUS-guided tissue acquisition?",
      "Show the PRISMA flow diagram for study selection in EUS guidelines.",
      "What is the evidence quality for 22-gauge versus 25-gauge needles in EUS tissue acquisition?",
      "What are the PICO questions in EUS tissue acquisition guidelines?",
    ],
  },
  {
    icon: FileCheck,
    label: "Preauthorization",
    intent: "preauthorization",
    queries: [
      "What documentation is required for prior authorization of a biologic medication for rheumatoid arthritis?",
      "What documentation is required for prior authorization of an implantable cardioverter-defibrillator for primary prevention in a patient with ischemic cardiomyopathy?",
      "What documentation is needed for prior authorization of endoscopic submucosal dissection for Barrett's esophagus with high-grade dysplasia?",
      "What criteria must be met for prior authorization of radiofrequency ablation as endoscopic eradication therapy for Barrett's esophagus?",
    ],
  },
  {
    icon: TestTube,
    label: "Test Interpretation",
    intent: "diagnostic_test_interpretation",
    queries: [
      "How do I interpret elevated troponin levels in the context of chest pain workup?",
      "47 year old man with mildly elevated alkaline phosphatase of 160. What is the appropriate line of investigations?",
      "How do I interpret a positive fecal immunochemical test (FIT) result, and what is the appropriate next step?",
      "How do I interpret EUS-guided fine needle biopsy results in a patient with suspected pancreatic malignancy?",
      "How do I interpret cardiac MRI findings showing myocardial fibrosis in a patient with hypertrophic cardiomyopathy and recurrent NSVT?",
    ],
  },
  {
    icon: Syringe,
    label: "Procedure Technique",
    intent: "procedure_technique",
    queries: [
      "What is the step-by-step technique for performing a lumbar puncture?",
      "When should endoscopic mucosal resection be used for treatment of a patient with Barrett's esophagus?",
      "When should endoscopic submucosal dissection be used for treatment of a patient with Barrett's esophagus?",
      "A 60-year-old Filipino woman is scheduled for a colonoscopy next month after a positive FIT. She asks whether split-dose bowel preparation is superior to a single-dose regimen, and when she should start taking it, considering her colonoscopy is scheduled for Friday at 12:00. She also asks about the best diet in the days preceding the colonoscopy.",
      "What is the most appropriate technique for removing non-pedunculated adenomatous polyps, non-pedunculated adenomatous polyps measuring 20 mm, and diminutive polyps?",
      "What endoscopic procedures are considered high-risk, and which are considered low-risk in terms of the decision to continue or suspend anticoagulant therapy?",
      "A 60-year-old Italian woman is taking cardioaspirin for the primary prevention of cardiac events, as prescribed by her cardiologist. She has hypertension, dyslipidemia, type 2 diabetes, and grade I obesity. One month ago, her FIT turned positive. She is scheduled for a colonoscopy in one week. Would you recommend that she suspend cardioaspirin? If yes, for how long?",
      "A 70-year-old Indian woman is taking warfarin as an anticoagulant for poorly controlled atrial fibrillation. She also has hypertension and experienced a TIA two years ago. One month ago, her FIT turned positive. She is scheduled for a colonoscopy in one week. Would you recommend that she suspend warfarin? If yes, for how long? What other recommendations would you provide?",
    ],
  },
  {
    icon: UserPlus,
    label: "Referral Criteria",
    intent: "referral_criteria",
    queries: [
      "When should I refer a patient with chronic kidney disease to a nephrologist?",
      "When should I refer a patient with Barrett's esophagus and confirmed dysplasia to an advanced endoscopist for evaluation and treatment?",
      "When should I refer a patient with atrial fibrillation to an electrophysiologist for ablation evaluation?",
      "When should I refer a patient with a large or complex colonic polyp to an endoscopist experienced in advanced resection techniques such as EMR or ESD?",
    ],
  },
/*  {
    icon: Calculator,
    label: "Clinical Calculators",
    intent: "clinical_scoring_calculators",
    queries: [
      "How do I calculate the CHA2DS2-VASc score for stroke risk in atrial fibrillation?",
      "How do I calculate the HAS-BLED score to assess a patient's bleeding risk before suspending anticoagulation for an endoscopic procedure?",
      "How do I apply the Prague C&M criteria to classify Barrett's esophagus extent and guide surveillance intervals?",
      "How do I use the HCM Risk-SCD calculator to estimate sudden cardiac death risk in a patient with hypertrophic cardiomyopathy?",
    ],
  },*/
];

function Suggestions({ onSuggestionClick }) {
  const [openGroup, setOpenGroup] = useState(null);
  const [openSub, setOpenSub] = useState(null);

  return (
    <div className="suggestions-container">
      <div className="suggestions-header">
        <span className="suggestions-title">Frequently Asked Clinical Questions</span>
      </div>

      {/* Level 1: Group buttons in a row */}
      <div className="suggestion-groups">
        {groups.map((group, gIdx) => (
          <button
            key={group.label}
            className={`group-btn${openGroup === gIdx ? " group-btn--active" : ""}`}
            onClick={() => {
              setOpenGroup(openGroup === gIdx ? null : gIdx);
              setOpenSub(null);
            }}
          >
            {group.label}
            <ChevronDown className={`group-chevron${openGroup === gIdx ? " group-chevron--open" : ""}`} />
          </button>
        ))}
      </div>

      {/* Level 2: Subcategory buttons */}
      {openGroup !== null && (
        <>
          <div className="suggestion-subcategories">
            {groups[openGroup].indices.map((index) => {
              const item = suggestions[index];
              const Icon = item.icon;
              return (
                <button
                  key={index}
                  className={`tab-btn${openSub === index ? " tab-btn--active" : ""}`}
                  onClick={() => setOpenSub(openSub === index ? null : index)}
                >
                  <Icon className="tab-icon" />
                  {item.label}
                  <ChevronDown className={`tab-chevron${openSub === index ? " tab-chevron--open" : ""}`} />
                </button>
              );
            })}
          </div>

          {/* Level 3: Questions – full width below the grid */}
          {openSub !== null && (
            <div className="suggestion-inline-queries">
              {suggestions[openSub].queries.map((query, qIndex) => (
                <button
                  key={qIndex}
                  className="suggestion-inline-query"
                  onClick={() => {
                    onSuggestionClick(query);
                    setOpenGroup(null);
                    setOpenSub(null);
                  }}
                >
                  {query}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Suggestions;
